'use client';
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Layers, LayoutGrid, Shield, Zap, ArrowRight, CheckCircle2, ChevronRight } from 'lucide-react';
import CTASection from '@/components/sections/CTASection';

import JsonLd from '@/components/common/JsonLd';

const services = [
  {
    title: "Roll Form Labels",
    description: "Engineered for high-volume automated production lines. Our roll labels offer seamless integration with industrial applicators, ensuring speed and precision for FMCG, Beverage, and Logistics.",
    icon: Layers,
    href: "/services/roll-form-labels",
    image: "/images/roll-labels-service.jpg",
    features: ["High-speed application", "Cost-effective for large runs", "Multiple finishing options"],
    accent: "from-blue-600 to-indigo-600"
  },
  {
    title: "Sheet Form Labels",
    description: "Perfect for manual application and small-to-medium batch sizes. We deliver premium-grade sheets with precision die-cutting, ideal for artisanal foods, cosmetics, and boutique retail products.",
    icon: LayoutGrid,
    href: "/services/sheet-form-labels",
    image: "/images/cosmatics.jpg",
    features: ["Easy manual handling", "No minimum order quantities", "Premium material variety"],
    accent: "from-teal-500 to-emerald-600"
  },
  {
    title: "Security & Promotional Labels",
    description: "Protect your brand with advanced security features including tamper-evident seals, holographic foils, and integrated QR tracking. Enhance engagement with vibrant promotional finishes.",
    icon: Shield,
    href: "/services/security-labels",
    image: "/images/security.png",
    features: ["Tamper-evident technology", "Holographic security foils", "Variable data integration"],
    accent: "from-purple-600 to-pink-600"
  },
  {
    title: "Digital Packaging Labels",
    description: "On-demand high-resolution digital printing for fast turnarounds, custom shapes, and variable design packaging. Ideal for short runs, seasonal products, and multi-SKU product lines.",
    icon: Zap,
    href: "/services/digital-packaging-labels",
    image: "/images/service_barcode_labels.png",
    features: ["Zero setup/plate costs", "Variable data & serialization", "Vibrant photographic quality"],
    accent: "from-amber-500 to-orange-600"
  },
];

const ServicesContent = () => {
  return (
    <>
      <JsonLd 
        type="OfferCatalog" 
        data={{
          name: "Industrial Labeling & Packaging Services",
          items: services.map(s => ({
            name: s.title,
            description: s.description,
            url: s.href
          }))
        }} 
      />
      {/* Hero Section */}
      <section
        className="rpl-hero-section w-full relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/premium_factory_roll_labels.png')" }}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/25 z-0" />

        {/* Breadcrumb Wrapper for perfect vertical alignment */}
        <div className="rpl-hero-breadcrumb-wrapper">
          <div className="rpl-hero-breadcrumb-container">
            <nav className="rpl-hero-breadcrumb">
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-white">Our Services</span>
            </nav>
          </div>
        </div>

        <div className="rpl-hero-frame">
          {/* Hero Card */}
          <div className="rpl-hero-card">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="rpl-hero-card-inner rpl-service-card-inner"
            >
              {/* Ambient highlight line */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

              <div className="flex flex-col items-start w-full relative z-10 gap-fluid-lg">
                <div className="flex flex-col gap-fluid-md w-full items-start">
                  {/* Badge */}
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-xs font-bold tracking-[3px] uppercase w-fit"
                  >
                    World-Class Labeling
                  </motion.span>

                  {/* Heading */}
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="rpl-hero-headline text-left"
                    style={{ color: '#ffffff' }}
                  >
                    <span className="block">Transforming Packaging</span>
                    <span className="block text-white/80 font-serif italic">with Precision.</span>
                  </motion.h1>

                  {/* Body */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="rpl-hero-body text-white/85 text-left"
                  >
                    From industrial-scale roll form solutions to high-security holographic seals, we empower brands across 10+ industries with labeling excellence.
                  </motion.p>
                </div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="rpl-hero-buttons"
                >
                  <Link href="/contact" className="no-tap-enforce w-full sm:w-auto">
                    <button className="rpl-infra-btn-primary">Start Your Project</button>
                  </Link>
                  <Link href="#services-grid" className="no-tap-enforce w-full sm:w-auto">
                    <button className="rpl-infra-btn-outline">View All Services</button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Services Grid */}
      <section className="w-full bg-white py-24 md:py-32 relative z-10" id="services-grid">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">

          {/* Services List Heading */}
          <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#09172e] mb-6">
              Our Core Services
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We offer a comprehensive range of labeling solutions tailored to meet the unique needs of every industry.
              Explore our specialized services below.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative flex flex-col bg-white rounded-[40px] overflow-hidden border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_40px_80px_rgba(0,0,0,0.1)] transition-all duration-500"
              >
                {/* Image Section */}
                <div className="relative h-[300px] md:h-[350px] overflow-hidden bg-gray-50 flex items-center justify-center">
                  <motion.img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-1000 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>

                {/* Content Section */}
                <div className="p-8 md:p-12 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-6">
                    <h3 className="text-3xl md:text-4xl font-display font-bold text-[#09172e] group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                  </div>

                  <p className="text-gray-600 text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>

                  <div className="mb-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-3">
                        <CheckCircle2 size={18} className="text-teal-500" />
                        <span className="text-sm font-medium text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto">
                    <Link href={service.href} className="group/link inline-flex items-center gap-4 py-4 px-8 bg-gray-50 text-gray-900 rounded-full font-bold transition-all duration-300 hover:bg-[#09172e] hover:text-white">
                      Explore Service
                      <ChevronRight size={20} className="transform group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Start Your Project?"
        description="Whether you need industrial scale roll production or boutique manual-application labels, our experts are here to help you find the perfect solution."
        buttonText="Get a Custom Quote"
        href="/contact"
      />
    </>
  );
};

export default ServicesContent;
